import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, AlertTriangle, Coins } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const CREDIT_COST = 10; // ₹10 per credit
const MAX_CREDITS = 100;

export function CreditPurchaseModal({ isOpen, onClose, onPurchase }) {
  const [creditAmount, setCreditAmount] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCreditAmount(1);
      setError('');
    }
  }, [isOpen]);

  const handleCreditChange = e => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value)) {
      setCreditAmount(1);
      return;
    }

    if (value < 1) {
      setError('Minimum 1 credit required');
      setCreditAmount(1);
    } else if (value > MAX_CREDITS) {
      setError(`Maximum ${MAX_CREDITS} credits allowed per transaction`);
      setCreditAmount(MAX_CREDITS);
    } else {
      setError('');
      setCreditAmount(value);
    }
  };

  const totalCost = creditAmount * CREDIT_COST;

  const handlePurchase = async () => {
    setIsProcessing(true);
    setError('');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/payment/createOrder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ amount: totalCost })
        }
      );

      const data = await response.json();
      // console.log(data);

      const paymentData = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        order_id: data.id,

        handler: async function (response) {
          const verifyResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_API_URL}/api/payment/verifypayment`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                orderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature
              })
            }
          );

          const verifyData = await verifyResponse.json();
          // console.log(verifyData);

          if (verifyData.isOk) {
            // do whatever page transition you want here as payment was successful
            // Here you would integrate with your payment gateway
            await onPurchase(creditAmount);
            onClose();
          } else {
            setError(error.message);
          }
        }
      };

      const payment = new window.Razorpay(paymentData);
      payment.open();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Helmet>
        <script
          src="https://checkout.razorpay.com/v1/checkout.js"
          type="text/javascript"
        />
      </Helmet>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
            >
              {/* Header */}
              <div className="border-b p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Coins className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-lg font-semibold">Purchase Credits</h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Credit Input */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Number of Credits
                  </label>
                  <input
                    type="number"
                    value={creditAmount}
                    onChange={handleCreditChange}
                    min="1"
                    max={MAX_CREDITS}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="text-sm text-gray-500">
                    ₹{CREDIT_COST} per credit
                  </p>
                </div>

                {/* Monthly Expiration Warning */}
                {/* <div className="bg-amber-50 rounded-lg p-4 flex gap-3">
                  <Calendar className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800 font-medium">
                      Monthly Credit Expiration
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      All purchased credits will expire at the end of the
                      current month and reset to your plan's base credits.
                    </p>
                  </div>
                </div> */}

                {/* Cost Breakdown */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Credits:</span>
                    <span className="font-medium">{creditAmount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Cost per credit:</span>
                    <span className="font-medium">₹{CREDIT_COST}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Total Amount:</span>
                    <span className="text-emerald-600">₹{totalCost}</span>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    <p>{error}</p>
                  </div>
                )}

                {/* Purchase Button */}
                <button
                  onClick={handlePurchase}
                  disabled={isProcessing || error || creditAmount < 1}
                  className="w-full py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Pay ₹{totalCost}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
